import { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import { Snapshot } from "../../../types/snapshot";
import { ImageBlock } from "../../../types/storyMap";
import { useUploadStoryMedia } from "../../../api/storyMaps";
import { entryProjectId } from "../layerEntries";
import type { ScopeLists } from "../useScopeLists";
import type { StoryScopeInfo } from "../useStoryScope";
import { dataUrlByteLength, toUploadableDataUrl } from "./downscaleImage";
import ProjectSelect from "./ProjectSelect";
import SnapshotSelect from "./SnapshotSelect";

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp"];
/**
 * Source-file ceiling. Images are downscaled and re-encoded before upload, so
 * what goes on the wire is far smaller than this — and the backend's own limit
 * (MAX_MEDIA_BYTES, with DATA_UPLOAD_MAX_MEMORY_SIZE raised to cover the
 * base64 inflation) now genuinely accepts a file this size either way.
 */
const MAX_BYTES = 10 * 1024 * 1024; // 10MB
/** What the server accepts after decoding; the client checks before sending. */
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

interface ImageBlockEditorProps {
  block: ImageBlock;
  onChange: (patch: Partial<ImageBlock>) => void;
  scope: StoryScopeInfo;
  lists: ScopeLists;
  storyMapId?: string;
}

const NO_SNAPSHOTS: Snapshot[] = [];

/**
 * Image block sources: a project snapshot, or an uploaded file (png/jpeg/webp
 * ≤10MB) stored via the story-map media endpoint as a permanent public URL.
 * Folder and organisation-wide stories pick the snapshot's project first.
 */
export default function ImageBlockEditor({
  block,
  onChange,
  scope,
  lists,
  storyMapId,
}: ImageBlockEditorProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false);
  const upload = useUploadStoryMedia(storyMapId);
  const busy = preparing || upload.isPending;
  const blockProjectId = entryProjectId(block.snapshotId ? block : null, scope.defaultProjectId);
  const [pickedProjectId, setPickedProjectId] = useState<number | null>(blockProjectId);
  const projectId = blockProjectId ?? pickedProjectId;
  useEffect(() => {
    if (projectId !== null) lists.requestProject(projectId);
  }, [projectId, lists]);
  const snapshots = projectId !== null ? lists.snapshotsByProject[String(projectId)] ?? NO_SNAPSHOTS : NO_SNAPSHOTS;

  const handleFile = async (file: File | undefined) => {
    setUploadError(null);
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setUploadError("Only PNG, JPEG or WebP images are supported.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setUploadError("Image is too large — the limit is 10MB.");
      return;
    }

    setPreparing(true);
    let dataUrl: string;
    try {
      dataUrl = await toUploadableDataUrl(file);
    } catch {
      setPreparing(false);
      setUploadError("Couldn't read the file.");
      return;
    }
    setPreparing(false);

    if (dataUrlByteLength(dataUrl) > MAX_UPLOAD_BYTES) {
      setUploadError("Image is too large to upload — try a smaller file.");
      return;
    }

    upload.mutate(dataUrl, {
      onSuccess: (response) => {
        const url = response.data?.url;
        if (url) {
          onChange({ url, snapshotId: null });
        } else {
          setUploadError("Upload failed — please try again.");
        }
      },
      onError: (error) =>
        setUploadError(error?.message || "Upload failed — please try again."),
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <ProjectSelect
        scope={scope}
        value={projectId}
        onChange={(next) => {
          setPickedProjectId(next);
          if (block.snapshotId) onChange({ snapshotId: null, projectId: undefined });
        }}
      />
      <SnapshotSelect
        label="Snapshot"
        snapshots={snapshots}
        value={block.snapshotId}
        onChange={(snapshot) =>
          onChange({
            snapshotId: snapshot?.id ?? null,
            url: snapshot?.imageUrl ?? block.url,
            ...(scope.isScoped ? { projectId: snapshot && projectId !== null ? projectId : undefined } : {}),
          })
        }
      />
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          startIcon={
            busy ? <CircularProgress size={14} /> : <UploadFileIcon />
          }
          disabled={busy || !storyMapId}
          onClick={() => fileInputRef.current?.click()}
          sx={{ textTransform: "none", alignSelf: "flex-start" }}
        >
          {preparing ? "Preparing…" : upload.isPending ? "Uploading…" : "Upload image"}
        </Button>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          PNG, JPEG or WebP · up to 10MB · large images are resized
        </Typography>
      </Box>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(",")}
        hidden
        onChange={(e) => {
          void handleFile(e.target.files?.[0]);
          // Allow re-selecting the same file.
          e.target.value = "";
        }}
      />
      {uploadError && (
        <Typography variant="caption" sx={{ color: "error.main" }}>
          {uploadError}
        </Typography>
      )}
      {block.url && (
        <Box
          component="img"
          src={block.url}
          alt={block.caption}
          sx={{ width: "100%", borderRadius: 1 }}
        />
      )}
      <TextField
        label="Caption"
        size="small"
        fullWidth
        value={block.caption}
        onChange={(e) => onChange({ caption: e.target.value })}
      />
    </Box>
  );
}
